import { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  type PieLabelRenderProps,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartBar, ChartPie, SquareHalf } from '@phosphor-icons/react';
import { formatCurrency } from '@/components/common/Currency';
import { compareMoney, parseMoney, toMoneyNumber } from '@erp/shared-utils';
import type {
  ArAgingTotals,
  ArAgingCustomerRow,
  AgingBucketDefinition,
} from '@erp/rpc';
import { T, useGT } from 'gt-react';

type ChartType = 'bar' | 'stacked' | 'pie';

const CHART_COLORS = [
  'var(--color-chart-1, #22c55e)',
  'var(--color-chart-2, #3b82f6)',
  'var(--color-chart-3, #f59e0b)',
  'var(--color-chart-4, #f97316)',
  'var(--color-chart-5, #ef4444)',
  'var(--color-chart-6, #8b5cf6)',
  'var(--color-chart-7, #ec4899)',
  'var(--color-chart-8, #14b8a6)',
];

function formatChartCurrency(value: number): string {
  return formatCurrency(value, { currency: 'USD' });
}

function formatCompactCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

interface ARAgingChartProps {
  totals: ArAgingTotals | null;
  customers: ArAgingCustomerRow[];
  bucketDefinitions: AgingBucketDefinition[];
  isLoading: boolean;
}

export function ARAgingChart({
  totals,
  customers,
  bucketDefinitions,
  isLoading,
}: ARAgingChartProps) {
  const gt = useGT();
  const [chartType, setChartType] = useState<ChartType>('bar');

  const bucketLabels = useMemo(
    () => bucketDefinitions.map((d) => d.label),
    [bucketDefinitions],
  );

  const barData = useMemo(() => {
    if (!totals || !totals.bucketAmounts.length) return [];
    const parsed = totals.bucketAmounts.map((amount, i) => ({
      name: bucketLabels[i] ?? `Bucket ${i}`,
      value: toMoneyNumber(parseMoney(amount ?? '0')),
      fill: CHART_COLORS[i % CHART_COLORS.length],
    }));
    const sum = parsed.reduce((s, d) => s + d.value, 0);
    return parsed.map((d) => ({
      ...d,
      percent: sum > 0 ? d.value / sum : 0,
    }));
  }, [totals, bucketLabels]);

  const stackedData = useMemo(() => {
    if (!customers.length) return [];
    // Top 10 customers by total outstanding
    return [...customers]
      .sort((a, b) => compareMoney(b.totalOutstanding, a.totalOutstanding))
      .slice(0, 10)
      .map((c) => {
        const row: Record<string, string | number> = {
          name: c.customerName ?? '(Unknown)',
        };
        c.bucketAmounts.forEach((amount, i) => {
          row[`bucket_${i}`] = toMoneyNumber(parseMoney(amount ?? '0'));
        });
        return row;
      });
  }, [customers]);

  const pieData = useMemo(() => {
    return barData.filter((d) => d.value > 0);
  }, [barData]);

  const smallSliceCount = useMemo(() => {
    const total = pieData.reduce((s, d) => s + d.value, 0);
    if (total === 0) return 0;
    return pieData.filter((d) => d.value / total < 0.05).length;
  }, [pieData]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <T context="AR aging chart card title">Aging Overview</T>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <T context="AR aging chart card title">Aging Overview</T>
          </CardTitle>
          <div className="flex items-center gap-2">
            <ToggleGroup
              type="single"
              value={chartType}
              onValueChange={(v) => {
                if (v) setChartType(v as ChartType);
              }}
              size="sm"
              variant="outline"
            >
              <ToggleGroupItem
                value="bar"
                aria-label={gt('Bar chart', {
                  context: 'aria label for bar chart toggle button',
                })}
              >
                <ChartBar className="size-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="stacked"
                aria-label={gt('Stacked bar chart', {
                  context: 'aria label for stacked bar chart toggle button',
                })}
              >
                <SquareHalf className="size-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="pie"
                aria-label={gt('Pie chart', {
                  context: 'aria label for pie chart toggle button',
                })}
              >
                <ChartPie className="size-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          {chartType === 'bar' && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barData}
                margin={{ top: 24, right: 5, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis
                  tickFormatter={formatCompactCurrency}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(
                    value,
                    _name,
                    item: { payload?: { percent?: number } } | undefined,
                  ) => {
                    const percent = item?.payload?.percent ?? 0;
                    const rounded = Math.round(percent * 100);
                    const pctLabel =
                      percent > 0 && rounded === 0 ? '<1%' : `${rounded}%`;
                    return [
                      `${formatChartCurrency(Number(value))} (${pctLabel})`,
                      gt('Amount', {
                        context:
                          'label for amount in AR aging bar chart tooltip',
                      }),
                    ];
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {barData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.fill} />
                  ))}
                  <LabelList
                    dataKey="percent"
                    position="top"
                    formatter={(value) => {
                      const num = Number(value);
                      if (!(num > 0)) return '';
                      const rounded = Math.round(num * 100);
                      return rounded === 0 ? '<1%' : `${rounded}%`;
                    }}
                    style={{ fontSize: 12, fill: 'var(--foreground)' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          {chartType === 'stacked' && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stackedData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={formatCompactCurrency}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  width={120}
                />
                <Tooltip
                  formatter={(value, name) => {
                    const idx = parseInt(
                      String(name).replace('bucket_', ''),
                      10,
                    );
                    const label = bucketLabels[idx] ?? name;
                    return [formatChartCurrency(Number(value)), label];
                  }}
                />
                <Legend
                  formatter={(value: string) => {
                    const idx = parseInt(value.replace('bucket_', ''), 10);
                    return bucketLabels[idx] ?? value;
                  }}
                />
                {bucketDefinitions.map((_, i) => (
                  <Bar
                    key={`bucket_${i}`}
                    dataKey={`bucket_${i}`}
                    stackId="a"
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}

          {chartType === 'pie' && (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
                <Pie
                  data={pieData}
                  cx="35%"
                  cy="45%"
                  animationDuration={400}
                  labelLine={false}
                  label={({ percent }: PieLabelRenderProps) => {
                    const pct = percent ?? 0;
                    const hideSmall = smallSliceCount > 1 && pct < 0.05;
                    return hideSmall ? '' : `${(pct * 100).toFixed(0)}%`;
                  }}
                  outerRadius={110}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => {
                    const num = Number(value);
                    const total = pieData.reduce((s, d) => s + d.value, 0);
                    const pct =
                      total > 0 ? ((num / total) * 100).toFixed(1) : '0';
                    return [`${formatChartCurrency(num)} (${pct}%)`, name];
                  }}
                />
                <Legend
                  layout="vertical"
                  align="left"
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  formatter={(value: string) => {
                    const entry = pieData.find((d) => d.name === value);
                    if (!entry) return value;
                    const total = pieData.reduce((s, d) => s + d.value, 0);
                    const pct =
                      total > 0
                        ? ((entry.value / total) * 100).toFixed(1)
                        : '0';
                    return `${value} — ${formatChartCurrency(entry.value)} (${pct}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
